import { cloneFragment } from 'slate-react-old';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { Value } from 'slate-old';

import ComposerContext from 'components/composer/contexts/composer_context';
import CustomerContext from 'components/customer/customer_context';
import FullSerializer from 'components/text_editor/serializers/full_serializer';
import useIsFeatureEnabled from 'components/hooks/use_is_feature_enabled';

const SLATE_TRANSFER_TYPE = 'application/x-slate-fragment';

// New slate and old slate don't play well with each other when it comes to the clipboard. New slate and old slate have
// completely different data models, so what gets copied to the clipboard from an old Slate editor (such as in the
// answer panel) is not comprehensible to new Slate. If the `slateBeta` flag is enabled, we'll create an HTML snippet
// from the copied content and store it in the CustomerContext, which _can_ be accessed by the new Slate composer on
// paste if needed.
export default function useCopyForNewSlate(editorRef) {
  const isFeatureEnabled = useIsFeatureEnabled();

  const { isExperimentalEditorEnabled } = useContext(ComposerContext);
  const { copiedHtml, setCopiedHtml } = useContext(CustomerContext);

  // In order to prevent `onCopy` from changing unnecessarily, stick these params in a ref.
  const ref = useRef({ setCopiedHtml, copiedHtml });
  useEffect(() => {
    ref.current = { setCopiedHtml, copiedHtml };
  }, [copiedHtml, setCopiedHtml]);

  // If the user hits copy anywhere in the app besides an old slate editor, remove the copiedHtml if it exists...
  // we don't want agents accidentally pasting in stale copied old Slate html.
  useEffect(() => {
    const clearCopiedHtml = evt => {
      if (!ref.current.copiedHtml || evt.target.getAttribute('data-slate-content')) {
        return;
      }

      ref.current.setCopiedHtml('');
    };
    window.addEventListener('copy', clearCopiedHtml);
    return () => window.removeEventListener('copy', clearCopiedHtml);
  }, []);

  const onCopy = useCallback(
    evt => {
      // Clone fragment takes the current selection and creates a new fragment out of it, base64 encoding it and
      // putting it in the clipboardData for the event. Unfortunately there's no easy way to just.. get the
      // fragment. Have to go through the event.
      cloneFragment(evt, editorRef.current);
      const encoded = evt.clipboardData.getData(SLATE_TRANSFER_TYPE);
      if (!encoded) {
        return;
      }

      // Base 64 decode it
      const fragmentDocument = JSON.parse(decodeURIComponent(window.atob(encoded)));
      // The serializer expects a Value, not a plain object. Of course the fragment isn't quite a value..
      // it's just the document part. Thanks Slate.
      const html = FullSerializer.serialize(Value.fromJSON({ document: fragmentDocument }));
      ref.current.setCopiedHtml(html);
    },
    [editorRef]
  );

  // If the flag isn't enabled, let's not waste cycles doing this whole conversion process on copy. This might
  // be expensive to perform
  if (!isFeatureEnabled('slateBeta') && isExperimentalEditorEnabled) {
    return null;
  }

  return onCopy;
}
